import React, { FC, useCallback } from "react";

import {
  DataViewKey,
  KpkMedia,
  KpkMetaField,
  KpkMetaFieldType,
  useBounceSearch,
  useShareToken,
} from "@keepeek/commons";
import {
  BaseComponentProps,
  ElementMetaField,
  generateButtonSx,
} from "@keepeek/refront-components";
import { Box, Divider } from "@mui/material";
import clsx from "clsx";
import { useRouter } from "next/router";
import { useRecoilValue } from "recoil";

import { useCustomerTheme } from "../../lib/theme-utils";
import {
  FieldsSectionsItemSchema,
  Type,
} from "../../models/configuration/definitions/fieldsSections";
import { backContextState } from "../../providers/backContext/atoms";
import { configSectionPageElementSelector } from "../../providers/config/selectors";

export type MediaDetailMetadataInfoProps = {
  kpkMedia: KpkMedia;
  fieldsSectionsToDisplay: FieldsSectionsItemSchema[];
} & BaseComponentProps;

function isValidValue(metaField: KpkMetaField | undefined): boolean {
  if (!metaField) {
    return false;
  }
  if (metaField.type === KpkMetaFieldType.Number) {
    return metaField.value !== undefined && metaField.value !== null && metaField.value !== "";
  }

  if (metaField.type === KpkMetaFieldType.CheckBox) {
    return true;
  }

  return (
    (metaField.value !== undefined &&
      metaField.value !== null &&
      metaField.value.toString().trim() !== "") ||
    (!!metaField.value &&
      ((!Array.isArray(metaField.value) && metaField.value.length > 0) ||
        (Array.isArray(metaField.value) && metaField.value.length > 0)))
  );
}

const MediaDetailMetadataInfo: FC<React.PropsWithChildren<MediaDetailMetadataInfoProps>> =
  function MetadataInfo({ kpkMedia, fieldsSectionsToDisplay, className }) {
    const { pathname, query } = useRouter();
    const { theme } = useCustomerTheme();
    const backContext = useRecoilValue(backContextState);
    const handleBounceSearch = useBounceSearch(backContext.key || DataViewKey.SEARCH);
    const { isShare } = useShareToken(pathname, query.tokenId);
    const { thesaurusButtonsColor, showThesaurusDescriptionInTitle } = useRecoilValue(
      configSectionPageElementSelector,
    ) ?? {
      thesaurusButtonsColor: undefined,
      showThesaurusDescriptionInTitle: false,
    };

    const isValidSection = useCallback(
      (sectionValue: string): boolean => {
        if (!sectionValue || sectionValue === "") {
          return false;
        }
        let nbField = 0;
        let indexSection = fieldsSectionsToDisplay.findIndex(
          (fieldSection) => fieldSection.value === sectionValue,
        );

        const firstIndex = indexSection;

        for (indexSection; indexSection < fieldsSectionsToDisplay.length; indexSection += 1) {
          const fieldOrSection = fieldsSectionsToDisplay[indexSection];
          if (fieldOrSection.type === "section" && indexSection !== firstIndex) {
            break;
          }
          if (fieldOrSection.type === "field") {
            const metaField = kpkMedia.fields?.find((f) => f.id === fieldOrSection.value);
            if (isValidValue(metaField)) {
              nbField += 1;
            }
          }
        }
        return nbField !== 0;
      },
      [fieldsSectionsToDisplay, kpkMedia.fields],
    );

    const fieldsSectionsContent = fieldsSectionsToDisplay.map((fieldSection) => {
      if (fieldSection.type === Type.Field) {
        const metaField = kpkMedia.fields?.find((f) => f.id === fieldSection.value);
        if (metaField && isValidValue(metaField)) {
          return (
            <Box key={metaField.id} margin={1}>
              <ElementMetaField
                field={metaField}
                summaryDisplay={false}
                disableClamping={true}
                showThesaurusDescriptionInTitle={showThesaurusDescriptionInTitle}
                sx={generateButtonSx({
                  buttonStyle: thesaurusButtonsColor,
                  theme,
                  variant: "contained",
                  styleName: ".MuiChip-root",
                })}
                onClick={!isShare ? (value) => handleBounceSearch(metaField, value) : undefined}
              />
            </Box>
          );
        }
        return null;
      } else if (fieldSection.type === Type.Section && isValidSection(fieldSection.value)) {
        return (
          <Box
            key={fieldSection.value}
            className={"MediaDetailMetadataInfo-Section"}
            sx={{
              textTransform: "uppercase",
              fontWeight: "bold",
              margin: 1,
            }}
          >
            {fieldSection.value}
            <Divider light />
          </Box>
        );
      } else {
        return null;
      }
    });

    return (
      <Box
        className={clsx("MediaDetailMetadataInfo", className)}
        sx={{
          width: "100%",
          "& .MediaDetailMetadataInfo-Section:not(div:first-of-type)": {
            paddingTop: 2,
          },
        }}
      >
        {fieldsSectionsContent}
      </Box>
    );
  };

export default MediaDetailMetadataInfo;
